/**
 * PulseFit Routine Planner Controller
 *
 * Manages the interactive "Plan Routine" modal builder, allowing athletes to:
 * - Build custom multi-split workouts (Run, RoxZone, HYROX Stations, Rest)
 * - Dynamically configure Time (for Run/RoxZone/Rest/Stations) and Distance (m) or Reps (for Stations)
 * - Track a live Overall Time Total, Split Count, Distance, and Reps summary
 * - Edit, reorder, duplicate, or remove splits
 * - Save, load, modify, and delete planned routines (persisted locally + synced to Supabase cloud)
 * - Dispatch any planned routine directly to the Apple Watch via startFavoriteSimulation
 */

import {
  CANONICAL_HYROX_STATIONS,
  STATION_META,
  getStationBadgeHtml,
  normalizeStationName
} from '../telemetry/index.js';
import {
  startFavoriteSimulation
} from '../integrations/index.js';
import {
  generateTelemetryPrompt,
  generateRecommendationPrompt,
  callGeminiCoach
} from '../ai-coaching/index.js';
import {
  store,
  subscribeKey,
  saveStateToStorage
} from '../../store.js';
import { isDistanceStation, escapeHtml } from './workouts.js';
import { getStationAlternatives } from './editor.js';
import { showView } from './router.js';
import {
  getDailySubjectiveRecovery,
  calculatePerformanceMetrics,
  calculateAcuteRecoveryScore
} from './performance.js';

export const STATION_PLAN_DEFAULTS = {
  Run: { distance: 1000, reps: null, durationMin: 5, durationSec: 0, metricMode: 'distance' },
  RoxZone: { distance: 0, reps: null, durationMin: 1, durationSec: 0, metricMode: 'none' },
  Rest: { distance: 0, reps: null, durationMin: 1, durationSec: 0, metricMode: 'none' },
  SkiErg: { distance: 1000, reps: null, durationMin: 4, durationSec: 15, metricMode: 'distance' },
  'Sled Push': { distance: 50, reps: null, durationMin: 3, durationSec: 0, metricMode: 'distance' },
  'Sled Pull': { distance: 50, reps: null, durationMin: 3, durationSec: 30, metricMode: 'distance' },
  'Burpee Broad Jumps': { distance: 80, reps: 80, durationMin: 4, durationSec: 0, metricMode: 'reps' },
  Rowing: { distance: 1000, reps: null, durationMin: 4, durationSec: 15, metricMode: 'distance' },
  'Farmers Carry': { distance: 200, reps: null, durationMin: 2, durationSec: 0, metricMode: 'distance' },
  'Sandbag Lunges': { distance: 100, reps: null, durationMin: 4, durationSec: 0, metricMode: 'distance' },
  'Wall Balls': { distance: 100, reps: 100, durationMin: 4, durationSec: 30, metricMode: 'reps' }
};

let currentEditingPlanId = null;
let draftPlanSplits = [];
let isPlannerInitialized = false;
let selectedDashboardPlanId = null;
let isDashboardDrawerOpen = false;
let dashboardPlanSearchQuery = '';
let modalPlanSearchQuery = '';

/**
 * Formats total seconds into MM:SS or H:MM:SS.
 * @param {number} totalSeconds
 * @returns {string}
 */
export function formatPlanDuration(totalSeconds) {
  const sec = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const hrs = Math.floor(sec / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  const remSec = sec % 60;
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(remSec).padStart(2, '0')}`;
  }
  return `${String(mins).padStart(2, '0')}:${String(remSec).padStart(2, '0')}`;
}

/**
 * Resolves the base canonical station name (stripping ": Alternative" if present).
 * @param {string} stationName
 * @returns {string}
 */
function resolveBaseStationKey(stationName = '') {
  const raw = String(stationName || '').trim();
  const base = raw.includes(':') ? raw.split(':')[0].trim() : raw;
  return normalizeStationName(base) || base || 'SkiErg';
}

/**
 * Determines the metric mode ('distance' | 'reps' | 'none') for a split.
 * @param {string} splitType
 * @param {string} stationName
 * @returns {'distance'|'reps'|'none'}
 */
export function getSplitMetricMode(splitType, stationName = '') {
  if (splitType === 'RoxZone' || splitType === 'Rest') {
    return 'none';
  }
  if (splitType === 'Run') {
    return 'distance';
  }
  const isDist = isDistanceStation(stationName || 'SkiErg', 'Station');
  return isDist ? 'distance' : 'reps';
}

/**
 * Creates a normalized split object with sensible defaults for the given type & station.
 * @param {string} [splitType='Run']
 * @param {string} [stationName='SkiErg']
 * @param {number} [stageNum=1]
 * @returns {Object}
 */
export function createDefaultPlanSplit(splitType = 'Run', stationName = 'SkiErg', stageNum = 1) {
  const cleanType = ['Run', 'RoxZone', 'Station', 'Rest'].includes(splitType) ? splitType : 'Station';
  let resolvedStationName = stationName;
  if (cleanType === 'Run') resolvedStationName = 'Run';
  else if (cleanType === 'RoxZone') resolvedStationName = 'RoxZone';
  else if (cleanType === 'Rest') resolvedStationName = 'Rest';
  else if (!resolvedStationName || resolvedStationName === 'Run' || resolvedStationName === 'RoxZone' || resolvedStationName === 'Rest') {
    resolvedStationName = 'SkiErg';
  }

  const baseKey = cleanType === 'Station' ? resolveBaseStationKey(resolvedStationName) : cleanType;
  const defaults = STATION_PLAN_DEFAULTS[baseKey] || {
    distance: 100,
    reps: 50,
    durationMin: 3,
    durationSec: 0
  };

  const metricMode = getSplitMetricMode(cleanType, resolvedStationName);
  const metricValue = metricMode === 'distance'
    ? (defaults.distance ?? 1000)
    : (metricMode === 'reps' ? (defaults.reps ?? 80) : 0);

  const durationMin = Number.isFinite(defaults.durationMin) ? defaults.durationMin : 3;
  const durationSec = Number.isFinite(defaults.durationSec) ? defaults.durationSec : 0;
  const totalSec = durationMin * 60 + durationSec;

  return normalizePlanSplit({
    stageNum,
    type: cleanType,
    stationName: resolvedStationName,
    name: resolvedStationName,
    durationMin,
    durationSec,
    duration: totalSec,
    duration_seconds: totalSec,
    metricMode,
    metricValue,
    distance: metricMode === 'distance' ? metricValue : (metricMode === 'reps' ? metricValue : 0),
    distance_meters: metricMode === 'distance' ? metricValue : 0,
    reps: metricMode === 'reps' ? metricValue : null
  }, stageNum);
}

/**
 * Normalizes a split object to ensure consistent duration, distance, and reps fields.
 * @param {Object} rawSplit
 * @param {number} stageNum
 * @returns {Object}
 */
export function normalizePlanSplit(rawSplit = {}, stageNum = 1) {
  const rawType = String(rawSplit.type || rawSplit.stage_type || 'Run').trim();
  let cleanType = 'Station';
  if (/^run/i.test(rawType)) cleanType = 'Run';
  else if (/^rox/i.test(rawType)) cleanType = 'RoxZone';
  else if (/^rest/i.test(rawType)) cleanType = 'Rest';
  else if (rawType === 'Station' || rawType === 'Strength') cleanType = 'Station';

  let stationName = String(rawSplit.stationName || rawSplit.name || '').trim();
  if (cleanType === 'Run') stationName = 'Run';
  else if (cleanType === 'RoxZone') stationName = 'RoxZone';
  else if (cleanType === 'Rest') stationName = 'Rest';
  else if (!stationName || stationName === 'Run' || stationName === 'RoxZone' || stationName === 'Rest') {
    stationName = 'SkiErg';
  }

  const metricMode = getSplitMetricMode(cleanType, stationName);

  let durationMin = parseInt(rawSplit.durationMin, 10);
  let durationSec = parseInt(rawSplit.durationSec, 10);
  if (!Number.isFinite(durationMin) || !Number.isFinite(durationSec)) {
    const fallbackSec = Math.max(0, parseInt(rawSplit.duration_seconds ?? rawSplit.duration ?? 0, 10) || 0);
    durationMin = Number.isFinite(durationMin) ? Math.max(0, durationMin) : Math.floor(fallbackSec / 60);
    durationSec = Number.isFinite(durationSec) ? Math.max(0, Math.min(59, durationSec)) : (fallbackSec % 60);
  } else {
    durationMin = Math.max(0, durationMin);
    durationSec = Math.max(0, Math.min(59, durationSec));
  }
  const totalSec = durationMin * 60 + durationSec;

  let metricValue = 0;
  if (metricMode === 'distance') {
    const rawDist = rawSplit.metricValue !== undefined
      ? rawSplit.metricValue
      : (rawSplit.distance_meters !== undefined && rawSplit.distance_meters !== null && rawSplit.distance_meters > 0
        ? rawSplit.distance_meters
        : rawSplit.distance);
    metricValue = Math.max(0, parseInt(rawDist, 10) || 0);
  } else if (metricMode === 'reps') {
    const rawReps = rawSplit.metricValue !== undefined
      ? rawSplit.metricValue
      : (rawSplit.reps !== undefined && rawSplit.reps !== null && rawSplit.reps > 0
        ? rawSplit.reps
        : rawSplit.distance);
    metricValue = Math.max(0, parseInt(rawReps, 10) || 0);
  }

  return {
    stageNum,
    stage_index: stageNum,
    type: cleanType,
    stage_type: cleanType,
    name: stationName,
    stationName,
    durationMin,
    durationSec,
    duration: totalSec,
    duration_seconds: totalSec,
    metricMode,
    metricValue,
    // Store metricValue in distance for both distance & rep stations so Watch bridge & timeline badges work seamlessly
    distance: metricMode === 'none' ? 0 : metricValue,
    distance_meters: metricMode === 'distance' ? metricValue : 0,
    reps: metricMode === 'reps' ? metricValue : null,
    avgHr: Number(rawSplit.avgHr || rawSplit.avg_hr || 0)
  };
}

/**
 * Returns a copy of the current draft splits in the planner.
 * @returns {Array<Object>}
 */
export function getDraftPlanSplits() {
  return draftPlanSplits;
}

/**
 * Sets the current draft splits in the planner and re-renders.
 * @param {Array<Object>} splits
 */
export function setDraftPlanSplits(splits = []) {
  draftPlanSplits = (Array.isArray(splits) ? splits : []).map((s, idx) => normalizePlanSplit(s, idx + 1));
  renderPlanSplitsList();
  recalculatePlanTotals();
}

/**
 * Returns the ID of the plan currently being edited (or null for new plan).
 * @returns {string|null}
 */
export function getCurrentEditingPlanId() {
  return currentEditingPlanId;
}

/**
 * Builds HTML `<option>` elements for the station selector, including configured alternatives.
 * @param {string} selectedStation
 * @returns {string}
 */
function buildStationOptionsHtml(selectedStation = 'SkiErg') {
  const altsMap = typeof getStationAlternatives === 'function' ? getStationAlternatives() : {};
  const knownValues = new Set();
  let html = '<optgroup label="HYROX Stations">';

  CANONICAL_HYROX_STATIONS.forEach((station) => {
    knownValues.add(station);
    const isSel = selectedStation === station ? 'selected' : '';
    const isDist = isDistanceStation(station, 'Station');
    const unitHint = isDist ? 'm' : 'reps';
    html += `<option value="${escapeHtml(station)}" ${isSel}>${escapeHtml(station)} (${unitHint})</option>`;
  });
  html += '</optgroup>';

  CANONICAL_HYROX_STATIONS.forEach((station) => {
    const altKey = altsMap[station]
      ? station
      : (station === 'Burpee Broad Jumps' && altsMap['Burpee Broad Jump']
        ? 'Burpee Broad Jump'
        : (station === 'Farmers Carry' && altsMap["Farmer's Carry"] ? "Farmer's Carry" : station));
    const stationAlts = Array.isArray(altsMap[altKey]) ? altsMap[altKey] : [];
    if (stationAlts.length > 0) {
      html += `<optgroup label="${escapeHtml(station)} Alternatives">`;
      stationAlts.forEach((alt) => {
        const composite = `${station}: ${alt}`;
        knownValues.add(composite);
        const isSel = selectedStation === composite ? 'selected' : '';
        html += `<option value="${escapeHtml(composite)}" ${isSel}>${escapeHtml(composite)}</option>`;
      });
      html += '</optgroup>';
    }
  });

  if (selectedStation && !knownValues.has(selectedStation)) {
    html += `<optgroup label="Custom Station"><option value="${escapeHtml(selectedStation)}" selected>${escapeHtml(selectedStation)}</option></optgroup>`;
  }

  return html;
}

/**
 * Recalculates the live overall time total, split count, total distance, and total reps,
 * and updates the DOM summary banner inside #modal-plan-routine.
 * @returns {{ totalSeconds: number, formattedTime: string, splitCount: number, totalDistanceMeters: number, totalReps: number }}
 */
export function recalculatePlanTotals() {
  let totalSeconds = 0;
  let totalDistanceMeters = 0;
  let totalReps = 0;

  draftPlanSplits.forEach((s, idx) => {
    s.stageNum = idx + 1;
    s.stage_index = idx + 1;
    const min = Math.max(0, parseInt(s.durationMin, 10) || 0);
    const sec = Math.max(0, Math.min(59, parseInt(s.durationSec, 10) || 0));
    const dur = min * 60 + sec;
    s.durationMin = min;
    s.durationSec = sec;
    s.duration = dur;
    s.duration_seconds = dur;
    totalSeconds += dur;

    const mode = getSplitMetricMode(s.type, s.stationName);
    s.metricMode = mode;
    const val = Math.max(0, parseInt(s.metricValue, 10) || 0);
    if (mode === 'distance') {
      s.distance = val;
      s.distance_meters = val;
      s.reps = null;
      totalDistanceMeters += val;
    } else if (mode === 'reps') {
      s.distance = val;
      s.distance_meters = 0;
      s.reps = val;
      totalReps += val;
    } else {
      s.distance = 0;
      s.distance_meters = 0;
      s.reps = null;
    }
  });

  const formattedTime = formatPlanDuration(totalSeconds);
  const splitCount = draftPlanSplits.length;

  if (typeof document !== 'undefined') {
    const timeEl = document.getElementById('plan-total-time');
    const splitsEl = document.getElementById('plan-total-splits');
    const distEl = document.getElementById('plan-total-distance');
    const repsEl = document.getElementById('plan-total-reps');

    if (timeEl) timeEl.textContent = formattedTime;
    if (splitsEl) splitsEl.textContent = `${splitCount} ${splitCount === 1 ? 'Split' : 'Splits'}`;
    if (distEl) {
      distEl.textContent = totalDistanceMeters >= 1000
        ? `${(totalDistanceMeters / 1000).toFixed(2)} km (${totalDistanceMeters.toLocaleString()} m)`
        : `${totalDistanceMeters.toLocaleString()} m`;
    }
    if (repsEl) {
      repsEl.textContent = `${totalReps.toLocaleString()} reps`;
    }
  }

  return {
    totalSeconds,
    formattedTime,
    splitCount,
    totalDistanceMeters,
    totalReps
  };
}

/**
 * Builds a sleek stacked dark-glass micro-stepper pair (▴ / ▾) matching the metric's accent color.
 * @param {number} idx
 * @param {'metricValue'|'durationMin'|'durationSec'} field
 * @param {number} stepDelta
 * @param {'stepper-cyan'|'stepper-amber'|'stepper-emerald'} themeClass
 * @returns {string}
 */
function buildMicroStepperHtml(idx, field, stepDelta, themeClass) {
  return `
    <span class="plan-num-stepper ${themeClass}">
      <button
        type="button"
        class="btn-plan-step"
        data-index="${idx}"
        data-field="${field}"
        data-delta="${stepDelta}"
        tabindex="-1"
        aria-label="Increase ${field}"
      >▴</button>
      <button
        type="button"
        class="btn-plan-step"
        data-index="${idx}"
        data-field="${field}"
        data-delta="${-stepDelta}"
        tabindex="-1"
        aria-label="Decrease ${field}"
      >▾</button>
    </span>
  `;
}

/**
 * Renders the interactive list of splits inside #plan-splits-list.
 */
export function renderPlanSplitsList() {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('plan-splits-list');
  if (!container) return;

  if (!Array.isArray(draftPlanSplits) || draftPlanSplits.length === 0) {
    container.innerHTML = `
      <div class="plan-splits-empty" style="text-align: center; padding: 24px 16px; border: 1px dashed rgba(255,255,255,0.12); border-radius: 10px; color: var(--text-muted); font-size: 13px;">
        No splits added yet. Use the quick-add buttons above (<strong>+ Run</strong>, <strong>+ RoxZone</strong>, <strong>+ Station</strong>, or <strong>⚡ Full HYROX</strong>) to build your workout!
      </div>
    `;
    recalculatePlanTotals();
    return;
  }

  container.innerHTML = draftPlanSplits.map((split, idx) => {
    const isStation = split.type === 'Station';
    const metricMode = getSplitMetricMode(split.type, split.stationName);
    const badgeKey = split.type === 'Run'
      ? 'Run'
      : (split.type === 'RoxZone' ? 'RoxZone' : (split.type === 'Rest' ? 'Rest' : (split.stationName || 'SkiErg')));
    const badgeHtml = getStationBadgeHtml(badgeKey, 'station-badge-xs');

    let metricFieldHtml = '';
    if (metricMode === 'distance') {
      const labelText = split.type === 'Run' ? 'Run Dist (m)' : 'Station Dist (m)';
      const baseSt = resolveBaseStationKey(split.stationName);
      const distStep = (baseSt === 'Sled Push' || baseSt === 'Sled Pull') ? 10 : 50;
      metricFieldHtml = `
        <div class="plan-split-field-group" style="display: flex; flex-direction: column; gap: 3px; min-width: 108px;">
          <label style="font-size: 9.5px; font-weight: 700; text-transform: uppercase; color: #38bdf8; letter-spacing: 0.4px;">${labelText}</label>
          <div class="plan-metric-pill plan-metric-pill-cyan" style="display: flex; align-items: center; gap: 4px; background: rgba(4, 12, 28, 0.55); border: 1px solid rgba(56, 189, 248, 0.32); border-radius: 8px; padding: 3px 6px 3px 8px;">
            <input
              type="number"
              class="plan-split-metric-input"
              data-index="${idx}"
              min="0"
              max="50000"
              step="10"
              value="${split.metricValue ?? 0}"
              aria-label="${labelText} for split ${idx + 1}"
              style="width: 48px; background: transparent; border: none; outline: none; color: var(--text-primary); font-size: 13px; font-weight: 700; font-family: monospace; text-align: right;"
            >
            ${buildMicroStepperHtml(idx, 'metricValue', distStep, 'stepper-cyan')}
            <span style="font-size: 11px; color: #7dd3fc; font-weight: 600;">m</span>
          </div>
        </div>
      `;
    } else if (metricMode === 'reps') {
      metricFieldHtml = `
        <div class="plan-split-field-group" style="display: flex; flex-direction: column; gap: 3px; min-width: 108px;">
          <label style="font-size: 9.5px; font-weight: 700; text-transform: uppercase; color: #f59e0b; letter-spacing: 0.4px;">Target Reps</label>
          <div class="plan-metric-pill plan-metric-pill-amber" style="display: flex; align-items: center; gap: 4px; background: rgba(24, 15, 4, 0.55); border: 1px solid rgba(245, 158, 11, 0.36); border-radius: 8px; padding: 3px 6px 3px 8px;">
            <input
              type="number"
              class="plan-split-metric-input"
              data-index="${idx}"
              min="0"
              max="5000"
              step="1"
              value="${split.metricValue ?? 0}"
              aria-label="Target reps for split ${idx + 1}"
              style="width: 44px; background: transparent; border: none; outline: none; color: var(--text-primary); font-size: 13px; font-weight: 700; font-family: monospace; text-align: right;"
            >
            ${buildMicroStepperHtml(idx, 'metricValue', 5, 'stepper-amber')}
            <span style="font-size: 11px; color: #fcd34d; font-weight: 600;">reps</span>
          </div>
        </div>
      `;
    } else {
      metricFieldHtml = `
        <div class="plan-split-field-group" style="display: flex; flex-direction: column; gap: 3px; min-width: 108px; opacity: 0.7;">
          <label style="font-size: 9.5px; font-weight: 700; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.4px;">Metric</label>
          <div style="display: flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, 0.45); border: 1px solid rgba(99, 102, 241, 0.16); border-radius: 8px; padding: 6px 8px; font-size: 11px; color: #94a3b8; height: 32px; box-sizing: border-box;">
            Time Only
          </div>
        </div>
      `;
    }

    const isFirst = idx === 0;
    const isLast = idx === draftPlanSplits.length - 1;

    return `
      <div class="plan-split-row" data-split-index="${idx}" style="display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; padding: 10px 12px; border-radius: 10px; background: rgba(15, 23, 42, 0.45); border: 1px solid rgba(99, 102, 241, 0.16);">
        <!-- Left: Split Index + Type + Station Select -->
        <div style="display: flex; align-items: center; gap: 8px; flex: 1 1 230px; min-width: 0; flex-wrap: wrap;">
          <span class="plan-split-num" style="font-size: 11px; font-weight: 800; color: var(--color-primary); background: rgba(99, 102, 241, 0.14); border: 1px solid rgba(99, 102, 241, 0.3); border-radius: 6px; padding: 4px 7px; font-family: monospace; flex-shrink: 0;">#${idx + 1}</span>
          <span style="flex-shrink: 0; display: inline-flex; align-items: center;">${badgeHtml}</span>
          <select
            class="plan-split-type-select"
            data-index="${idx}"
            aria-label="Split type for split ${idx + 1}"
            style="padding: 6px 26px 6px 9px; border-radius: 7px; background-color: rgba(8, 12, 24, 0.65); color: var(--text-primary); border: 1px solid rgba(99, 102, 241, 0.28); font-size: 12.5px; font-weight: 600; outline: none; cursor: pointer;"
          >
            <option value="Run" ${split.type === 'Run' ? 'selected' : ''}>🏃 Run</option>
            <option value="RoxZone" ${split.type === 'RoxZone' ? 'selected' : ''}>⚡ RoxZone</option>
            <option value="Station" ${split.type === 'Station' ? 'selected' : ''}>💪 Station</option>
            <option value="Rest" ${split.type === 'Rest' ? 'selected' : ''}>☕ Rest</option>
          </select>

          ${isStation ? `
            <select
              class="plan-split-station-select"
              data-index="${idx}"
              aria-label="Station selection for split ${idx + 1}"
              style="flex: 1 1 140px; min-width: 130px; padding: 6px 26px 6px 9px; border-radius: 7px; background-color: rgba(8, 12, 24, 0.65); color: var(--text-primary); border: 1px solid rgba(167, 139, 250, 0.38); font-size: 12.5px; font-weight: 600; outline: none; cursor: pointer;"
            >
              ${buildStationOptionsHtml(split.stationName)}
            </select>
          ` : ''}
        </div>

        <!-- Middle: Dynamic Metric (Distance / Reps) + Duration (Min / Sec) -->
        <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
          ${metricFieldHtml}

          <div class="plan-split-field-group" style="display: flex; flex-direction: column; gap: 3px;">
            <label style="font-size: 9.5px; font-weight: 700; text-transform: uppercase; color: #10b981; letter-spacing: 0.4px;">Split Time</label>
            <div class="plan-metric-pill plan-metric-pill-emerald" style="display: flex; align-items: center; gap: 4px; background: rgba(4, 22, 18, 0.55); border: 1px solid rgba(16, 185, 129, 0.34); border-radius: 8px; padding: 3px 7px;">
              <input
                type="number"
                class="plan-split-min-input"
                data-index="${idx}"
                min="0"
                max="999"
                value="${split.durationMin ?? 0}"
                aria-label="Minutes for split ${idx + 1}"
                style="width: 30px; background: transparent; border: none; outline: none; color: var(--text-primary); font-size: 13px; font-weight: 700; font-family: monospace; text-align: right;"
              >
              ${buildMicroStepperHtml(idx, 'durationMin', 1, 'stepper-emerald')}
              <span style="font-size: 11px; color: #6ee7b7; font-weight: 600; margin-right: 2px;">m</span>
              <input
                type="number"
                class="plan-split-sec-input"
                data-index="${idx}"
                min="0"
                max="59"
                value="${String(split.durationSec ?? 0).padStart(2, '0')}"
                aria-label="Seconds for split ${idx + 1}"
                style="width: 26px; background: transparent; border: none; outline: none; color: var(--text-primary); font-size: 13px; font-weight: 700; font-family: monospace; text-align: right;"
              >
              ${buildMicroStepperHtml(idx, 'durationSec', 5, 'stepper-emerald')}
              <span style="font-size: 11px; color: #6ee7b7; font-weight: 600;">s</span>
            </div>
          </div>

          <!-- Right: Reorder / Duplicate / Remove Actions -->
          <div style="display: flex; align-items: center; gap: 4px; margin-top: 14px;">
            <button
              type="button"
              class="btn-plan-split-up"
              data-index="${idx}"
              title="Move split up"
              ${isFirst ? 'disabled' : ''}
              style="background: ${isFirst ? 'rgba(15, 23, 42, 0.45)' : 'linear-gradient(135deg, rgba(56, 189, 248, 0.14) 0%, rgba(99, 102, 241, 0.16) 100%)'}; border: 1px solid ${isFirst ? 'rgba(99, 102, 241, 0.16)' : 'rgba(56, 189, 248, 0.38)'}; color: ${isFirst ? 'rgba(129, 140, 248, 0.35)' : '#38bdf8'}; border-radius: 6px; width: 28px; height: 28px; cursor: ${isFirst ? 'not-allowed' : 'pointer'}; opacity: ${isFirst ? '0.45' : '1'}; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;"
            >↑</button>
            <button
              type="button"
              class="btn-plan-split-down"
              data-index="${idx}"
              title="Move split down"
              ${isLast ? 'disabled' : ''}
              style="background: ${isLast ? 'rgba(15, 23, 42, 0.45)' : 'linear-gradient(135deg, rgba(56, 189, 248, 0.14) 0%, rgba(99, 102, 241, 0.16) 100%)'}; border: 1px solid ${isLast ? 'rgba(99, 102, 241, 0.16)' : 'rgba(56, 189, 248, 0.38)'}; color: ${isLast ? 'rgba(129, 140, 248, 0.35)' : '#38bdf8'}; border-radius: 6px; width: 28px; height: 28px; cursor: ${isLast ? 'not-allowed' : 'pointer'}; opacity: ${isLast ? '0.45' : '1'}; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;"
            >↓</button>
            <button
              type="button"
              class="btn-plan-split-dup"
              data-index="${idx}"
              title="Duplicate split"
              style="background: rgba(99, 102, 241, 0.14); border: 1px solid rgba(99, 102, 241, 0.32); color: #818cf8; border-radius: 6px; width: 28px; height: 28px; cursor: pointer; font-size: 12px; display: inline-flex; align-items: center; justify-content: center;"
            >⧉</button>
            <button
              type="button"
              class="btn-plan-split-remove"
              data-index="${idx}"
              title="Remove split"
              style="background: rgba(244, 63, 94, 0.12); border: 1px solid rgba(244, 63, 94, 0.3); color: #f43f5e; border-radius: 6px; width: 28px; height: 28px; cursor: pointer; font-size: 13px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;"
            >✕</button>
          </div>
        </div>
      </div>
    `;
  }).join('');

  recalculatePlanTotals();
}

/**
 * Adds a new split to the current planner draft.
 * @param {string} [splitType='Run']
 * @param {string} [stationName='SkiErg']
 * @returns {Object} The newly added split
 */
export function addPlanSplit(splitType = 'Run', stationName = 'SkiErg') {
  const nextNum = draftPlanSplits.length + 1;
  let resolvedStation = stationName;
  if (splitType === 'Station' && (!stationName || stationName === 'Auto')) {
    const existingStationCount = draftPlanSplits.filter(s => s.type === 'Station').length;
    resolvedStation = CANONICAL_HYROX_STATIONS[existingStationCount % CANONICAL_HYROX_STATIONS.length] || 'SkiErg';
  }
  const newSplit = createDefaultPlanSplit(splitType, resolvedStation, nextNum);
  draftPlanSplits.push(newSplit);
  renderPlanSplitsList();
  return newSplit;
}

/**
 * Populates a full 16-split canonical HYROX template (8x 1km Run + 8x HYROX Stations).
 * @returns {Array<Object>}
 */
export function loadHyroxPresetIntoPlanner() {
  const stages = [];
  CANONICAL_HYROX_STATIONS.forEach((station) => {
    stages.push(createDefaultPlanSplit('Run', 'Run', stages.length + 1));
    stages.push(createDefaultPlanSplit('Station', station, stages.length + 1));
  });
  draftPlanSplits = stages;

  if (typeof document !== 'undefined') {
    const nameInput = document.getElementById('plan-routine-name');
    if (nameInput && !nameInput.value.trim()) {
      nameInput.value = 'Full HYROX Simulation';
    }
  }

  renderPlanSplitsList();
  if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
    window.showToast('⚡ Loaded 16-split HYROX template!');
  }
  return draftPlanSplits;
}

/**
 * Clears all splits from the current draft.
 */
export function clearPlanSplits() {
  draftPlanSplits = [];
  renderPlanSplitsList();
}

/**
 * Updates a specific field on a split at index.
 * @param {number} index
 * @param {'type'|'stationName'|'durationMin'|'durationSec'|'metricValue'} field
 * @param {any} value
 */
export function updatePlanSplitField(index, field, value) {
  const split = draftPlanSplits[index];
  if (!split) return null;

  if (field === 'type') {
    const newType = ['Run', 'RoxZone', 'Station', 'Rest'].includes(value) ? value : 'Station';
    const nextStation = newType === 'Station'
      ? (split.type === 'Station' && split.stationName ? split.stationName : 'SkiErg')
      : newType;
    const replacement = createDefaultPlanSplit(newType, nextStation, index + 1);
    draftPlanSplits[index] = replacement;
    renderPlanSplitsList();
    return replacement;
  }

  if (field === 'stationName') {
    const newStation = String(value || 'SkiErg').trim();
    const baseKey = resolveBaseStationKey(newStation);
    const defaults = STATION_PLAN_DEFAULTS[baseKey] || { distance: 100, reps: 50, durationMin: 3, durationSec: 0 };
    const mode = getSplitMetricMode('Station', newStation);

    split.stationName = newStation;
    split.name = newStation;
    split.metricMode = mode;
    split.metricValue = mode === 'distance' ? (defaults.distance ?? 100) : (defaults.reps ?? 50);
    split.durationMin = defaults.durationMin ?? split.durationMin;
    split.durationSec = defaults.durationSec ?? split.durationSec;
    renderPlanSplitsList();
    return split;
  }

  if (field === 'durationMin') {
    split.durationMin = Math.max(0, parseInt(value, 10) || 0);
    recalculatePlanTotals();
    return split;
  }

  if (field === 'durationSec') {
    split.durationSec = Math.max(0, Math.min(59, parseInt(value, 10) || 0));
    recalculatePlanTotals();
    return split;
  }

  if (field === 'metricValue') {
    split.metricValue = Math.max(0, parseInt(value, 10) || 0);
    recalculatePlanTotals();
    return split;
  }

  return split;
}

/**
 * Steps a split's numeric field (metricValue, durationMin, durationSec) by delta and updates the DOM & totals.
 * @param {number} index
 * @param {'metricValue'|'durationMin'|'durationSec'} field
 * @param {number} delta
 */
export function stepPlanSplitField(index, field, delta) {
  const split = draftPlanSplits[index];
  if (!split) return null;
  const numDelta = parseInt(delta, 10) || 0;

  if (field === 'metricValue') {
    split.metricValue = Math.max(0, (parseInt(split.metricValue, 10) || 0) + numDelta);
  } else if (field === 'durationMin') {
    split.durationMin = Math.max(0, Math.min(999, (parseInt(split.durationMin, 10) || 0) + numDelta));
  } else if (field === 'durationSec') {
    const totalSec = Math.max(0, (parseInt(split.durationMin, 10) || 0) * 60 + (parseInt(split.durationSec, 10) || 0) + numDelta);
    split.durationMin = Math.min(999, Math.floor(totalSec / 60));
    split.durationSec = totalSec % 60;
  }

  renderPlanSplitsList();
  return split;
}

/**
 * Moves a split up (-1) or down (+1).
 * @param {number} index
 * @param {number} direction -1 or 1
 */
export function movePlanSplit(index, direction) {
  const targetIdx = index + direction;
  if (index < 0 || index >= draftPlanSplits.length) return false;
  if (targetIdx < 0 || targetIdx >= draftPlanSplits.length) return false;

  const temp = draftPlanSplits[index];
  draftPlanSplits[index] = draftPlanSplits[targetIdx];
  draftPlanSplits[targetIdx] = temp;
  renderPlanSplitsList();
  return true;
}

/**
 * Duplicates a split right after the given index.
 * @param {number} index
 */
export function duplicatePlanSplit(index) {
  if (index < 0 || index >= draftPlanSplits.length) return null;
  const copy = normalizePlanSplit({ ...draftPlanSplits[index] }, index + 2);
  draftPlanSplits.splice(index + 1, 0, copy);
  renderPlanSplitsList();
  return copy;
}

/**
 * Removes a split at the given index.
 * @param {number} index
 */
export function removePlanSplit(index) {
  if (index < 0 || index >= draftPlanSplits.length) return false;
  draftPlanSplits.splice(index, 1);
  renderPlanSplitsList();
  return true;
}

/**
 * Syncs a saved planned workout to Supabase `planned_workouts` table (best-effort) + profile cloud settings.
 * @param {Object} planObj
 */
async function syncPlannedWorkoutToCloud(planObj) {
  if (typeof window !== 'undefined' && typeof window.saveUserSettingsToCloud === 'function') {
    window.saveUserSettingsToCloud();
  }
  const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
  if (!supabase || !store.currentUser || !store.currentUser.id || store.isGuest || store.currentUser.id === 'guest-preview-user') {
    return;
  }
  try {
    const table = typeof supabase.from === 'function' ? supabase.from('planned_workouts') : null;
    if (table && typeof table.upsert === 'function') {
      await table.upsert({
        id: String(planObj.id),
        user_id: store.currentUser.id,
        name: planObj.name || 'Planned Routine',
        duration_seconds: Math.round(Number(planObj.durationSeconds) || 0),
        total_distance_meters: Number(planObj.totalDistanceMeters) || 0,
        stages: planObj.stages || [],
        updated_at: new Date().toISOString()
      }, { onConflict: 'id' });
    }
  } catch (err) {
    // Graceful fallback: profile avatar_url settings & localStorage already persist plannedWorkouts
    console.warn('[Planner] Optional planned_workouts table sync skipped:', err?.message || err);
  }
}

/**
 * Saves or updates the current draft routine into `store.plannedWorkouts`, `localStorage`, and Supabase.
 * @param {Object} [options={ closeAfterSave: false, silent: false }]
 * @returns {Promise<Object|null>}
 */
export async function savePlannedWorkout(options = {}) {
  const { closeAfterSave = false, silent = false } = options;

  if (!Array.isArray(draftPlanSplits) || draftPlanSplits.length === 0) {
    if (!silent && typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast('Please add at least one split before saving.', 'error');
    }
    return null;
  }

  const nameInput = typeof document !== 'undefined' ? document.getElementById('plan-routine-name') : null;
  const rawName = nameInput ? nameInput.value.trim() : '';
  const routineName = rawName || 'Planned Routine';
  if (nameInput && !rawName) {
    nameInput.value = routineName;
  }

  const totals = recalculatePlanTotals();
  const normalizedStages = draftPlanSplits.map((s, idx) => normalizePlanSplit(s, idx + 1));
  const nowIso = new Date().toISOString();

  if (!Array.isArray(store.plannedWorkouts)) {
    store.plannedWorkouts = [];
  }

  const existingIdx = currentEditingPlanId
    ? store.plannedWorkouts.findIndex(p => p && String(p.id) === String(currentEditingPlanId))
    : -1;
  const existingPlan = existingIdx !== -1 ? store.plannedWorkouts[existingIdx] : null;
  const planId = existingPlan ? existingPlan.id : (currentEditingPlanId || `plan-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);

  const planObj = {
    id: planId,
    name: routineName,
    type: 'Hyrox',
    isPlanned: true,
    duration: Math.max(1, Math.round((totals.totalSeconds / 60) * 10) / 10),
    durationSeconds: totals.totalSeconds,
    duration_seconds: totals.totalSeconds,
    distance: Math.round((totals.totalDistanceMeters / 1000) * 100) / 100,
    totalDistanceMeters: totals.totalDistanceMeters,
    totalReps: totals.totalReps,
    stages: normalizedStages,
    createdAt: existingPlan?.createdAt || nowIso,
    updatedAt: nowIso
  };

  if (existingIdx !== -1) {
    store.plannedWorkouts.splice(existingIdx, 1, planObj);
  } else {
    store.plannedWorkouts = [planObj, ...store.plannedWorkouts];
  }

  currentEditingPlanId = planId;
  saveStateToStorage();
  await syncPlannedWorkoutToCloud(planObj);

  updatePlannerModalHeaderState();
  renderSavedPlannedWorkouts();

  if (!silent && typeof window !== 'undefined' && typeof window.showToast === 'function') {
    window.showToast(`💾 Saved "${routineName}" (${normalizedStages.length} splits • ${totals.formattedTime})!`);
  }

  if (closeAfterSave) {
    closePlanRoutineModal();
  }

  return planObj;
}

/**
 * Deletes a saved planned workout by ID.
 * @param {string} planId
 */
export async function deletePlannedWorkout(planId) {
  if (!planId || !Array.isArray(store.plannedWorkouts)) return false;
  const idx = store.plannedWorkouts.findIndex(p => p && String(p.id) === String(planId));
  if (idx === -1) return false;

  const removed = store.plannedWorkouts[idx];
  store.plannedWorkouts = store.plannedWorkouts.filter(p => p && String(p.id) !== String(planId));

  if (String(currentEditingPlanId) === String(planId)) {
    currentEditingPlanId = null;
    updatePlannerModalHeaderState();
  }

  saveStateToStorage();
  if (typeof window !== 'undefined' && typeof window.saveUserSettingsToCloud === 'function') {
    window.saveUserSettingsToCloud();
  }

  const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
  if (supabase && store.currentUser && store.currentUser.id && !store.isGuest && store.currentUser.id !== 'guest-preview-user') {
    try {
      const table = typeof supabase.from === 'function' ? supabase.from('planned_workouts') : null;
      if (table && typeof table.delete === 'function') {
        await table.delete().eq('id', String(planId)).eq('user_id', store.currentUser.id);
      }
    } catch (err) {
      console.warn('[Planner] Optional planned_workouts delete skipped:', err?.message || err);
    }
  }

  renderSavedPlannedWorkouts();
  if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
    window.showToast(`🗑️ Deleted "${removed?.name || 'Planned Routine'}"`);
  }
  return true;
}

/**
 * Sends a planned workout (either by ID or the active draft in the modal) to the Apple Watch
 * using the existing `startFavoriteSimulation` bridge.
 * @param {string|null} [planId=null]
 * @returns {Promise<boolean>}
 */
export async function sendPlannedWorkoutToWatch(planId = null) {
  let targetPlan = null;

  if (planId) {
    const list = Array.isArray(store.plannedWorkouts) ? store.plannedWorkouts : [];
    targetPlan = list.find(p => p && String(p.id) === String(planId)) || null;
  } else {
    // Save current draft automatically before sending to Watch
    targetPlan = await savePlannedWorkout({ closeAfterSave: false, silent: true });
  }

  if (!targetPlan) {
    if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
      window.showToast('Unable to find or save routine to send to Watch.', 'error');
    }
    return false;
  }

  return startFavoriteSimulation(targetPlan);
}

/**
 * Updates the modal title and Save button text depending on whether we are creating or editing.
 */
function updatePlannerModalHeaderState() {
  if (typeof document === 'undefined') return;
  const titleEl = document.getElementById('modal-plan-routine-title');
  const saveBtn = document.getElementById('btn-save-planned-routine');
  const statusBadge = document.getElementById('plan-editing-status-badge');

  if (currentEditingPlanId) {
    if (titleEl) titleEl.innerHTML = '<span>✏️ Edit Planned Routine</span>';
    if (saveBtn) saveBtn.innerHTML = '<span>💾 Update Routine</span>';
    if (statusBadge) {
      statusBadge.style.display = 'inline-flex';
      statusBadge.textContent = 'Editing Saved Plan';
    }
  } else {
    if (titleEl) titleEl.innerHTML = '<span>📋 Plan Workout Routine</span>';
    if (saveBtn) saveBtn.innerHTML = '<span>💾 Save Routine</span>';
    if (statusBadge) {
      statusBadge.style.display = 'none';
    }
  }
}

/**
 * Opens the Plan Routine modal, either for a new routine or populated with an existing saved routine.
 * @param {string|null} [planId=null]
 */
export function openPlanRoutineModal(planId = null) {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('modal-plan-routine');
  if (!modal) return;

  const nameInput = document.getElementById('plan-routine-name');
  const plannedList = Array.isArray(store.plannedWorkouts) ? store.plannedWorkouts : [];

  if (planId) {
    const existing = plannedList.find(p => p && String(p.id) === String(planId));
    if (existing) {
      currentEditingPlanId = existing.id;
      if (nameInput) nameInput.value = existing.name || 'Planned Routine';
      draftPlanSplits = (Array.isArray(existing.stages) ? existing.stages : []).map((s, idx) =>
        normalizePlanSplit(s, idx + 1)
      );
    }
  } else if (!currentEditingPlanId && draftPlanSplits.length === 0) {
    if (nameInput) nameInput.value = '';
    draftPlanSplits = [
      createDefaultPlanSplit('Run', 'Run', 1),
      createDefaultPlanSplit('Station', 'SkiErg', 2)
    ];
  } else if (planId === null && currentEditingPlanId !== null) {
    // Switching from editing back to a fresh draft when clicking "Plan" on dashboard
    currentEditingPlanId = null;
    if (nameInput) nameInput.value = '';
    draftPlanSplits = [
      createDefaultPlanSplit('Run', 'Run', 1),
      createDefaultPlanSplit('Station', 'SkiErg', 2)
    ];
  }

  updatePlannerModalHeaderState();
  renderPlanSplitsList();
  renderSavedPlannedWorkouts();

  modal.style.display = 'flex';
  if (typeof modal.showModal === 'function' && !modal.open) {
    modal.showModal();
  }
}

/**
 * Resets the modal builder to a blank new routine draft.
 */
export function startNewPlanRoutineDraft() {
  currentEditingPlanId = null;
  if (typeof document !== 'undefined') {
    const nameInput = document.getElementById('plan-routine-name');
    if (nameInput) nameInput.value = '';
  }
  draftPlanSplits = [
    createDefaultPlanSplit('Run', 'Run', 1)
  ];
  updatePlannerModalHeaderState();
  renderPlanSplitsList();
}

/**
 * Closes the Plan Routine modal.
 */
export function closePlanRoutineModal() {
  if (typeof document === 'undefined') return;
  const modal = document.getElementById('modal-plan-routine');
  if (!modal) return;
  if (typeof modal.close === 'function' && modal.open) {
    modal.close();
  }
  modal.style.display = 'none';
}

/**
 * Builds a concise 1-line text summary of a routine's splits (used for tooltips & compact subtext).
 * @param {Array<Object>} stages
 * @returns {string}
 */
function buildCompactStageSequenceText(stages = []) {
  if (!Array.isArray(stages) || stages.length === 0) return 'No splits';
  return stages.map((st) => {
    const isRun = st.type === 'Run';
    const isRox = st.type === 'RoxZone';
    const isRest = st.type === 'Rest';
    const label = isRun ? 'Run' : (isRox ? 'RoxZone' : (isRest ? 'Rest' : (st.stationName || st.name || 'Station')));
    const mode = getSplitMetricMode(st.type, st.stationName);
    const val = mode === 'reps' ? (st.reps || st.distance || 0) : (st.distance || st.distance_meters || 0);
    const metricText = mode === 'distance' && val > 0 ? ` ${val}m` : (mode === 'reps' && val > 0 ? ` ${val}r` : '');
    return `${label}${metricText}`;
  }).join(' → ');
}

/**
 * Builds ultra-compact inline chips (max 3 chips + +N badge on a single line) for the active routine.
 * @param {Array<Object>} stages
 * @param {number} [maxPreview=3]
 * @returns {string}
 */
function buildSavedPlanStagePillsHtml(stages = [], maxPreview = 3) {
  if (!Array.isArray(stages) || stages.length === 0) return '';
  const preview = stages.slice(0, maxPreview).map((st) => {
    const isRun = st.type === 'Run';
    const isRox = st.type === 'RoxZone';
    const isRest = st.type === 'Rest';
    const label = isRun ? '🏃 Run' : (isRox ? '⚡ RoxZone' : (isRest ? '☕ Rest' : `💪 ${escapeHtml(st.stationName || st.name || 'Station')}`));
    const mode = getSplitMetricMode(st.type, st.stationName);
    const val = mode === 'reps' ? (st.reps || st.distance || 0) : (st.distance || st.distance_meters || 0);
    const metricText = mode === 'distance' && val > 0 ? ` ${val}m` : (mode === 'reps' && val > 0 ? ` ${val}r` : '');
    return `<span style="font-size: 10px; padding: 2px 6px; border-radius: 999px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); color: var(--text-secondary); white-space: nowrap;">${label}${metricText}</span>`;
  }).join('');

  const extra = stages.length > maxPreview
    ? `<span style="font-size: 10px; padding: 2px 6px; border-radius: 999px; background: rgba(99,102,241,0.14); color: #a5b4fc; font-weight: 600; white-space: nowrap;">+${stages.length - maxPreview}</span>`
    : '';

  return `<div style="display: inline-flex; align-items: center; gap: 4px; overflow: hidden; max-width: 100%;">${preview}${extra}</div>`;
}

/**
 * Selects which saved routine is active in the Dashboard 1-line Quick-Launcher Bar.
 * @param {string} planId
 */
export function selectDashboardPlannedWorkout(planId) {
  selectedDashboardPlanId = planId ? String(planId) : null;
  renderSavedPlannedWorkouts();
}

/**
 * Toggles (or sets) whether the collapsible "Browse All" drawer on the Dashboard is expanded.
 * @param {boolean} [forceState]
 * @returns {boolean}
 */
export function toggleDashboardPlansDrawer(forceState) {
  isDashboardDrawerOpen = typeof forceState === 'boolean' ? forceState : !isDashboardDrawerOpen;
  renderSavedPlannedWorkouts();
  return isDashboardDrawerOpen;
}

/**
 * Updates the search filter query for the Dashboard collapsible saved routines drawer.
 * @param {string} query
 */
export function setDashboardPlanSearchQuery(query = '') {
  dashboardPlanSearchQuery = String(query || '');
  renderSavedPlannedWorkouts({ preserveSearchFocus: 'dashboard' });
}

/**
 * Updates the search filter query for the Modal saved routines library.
 * @param {string} query
 */
export function setModalPlanSearchQuery(query = '') {
  modalPlanSearchQuery = String(query || '');
  renderSavedPlannedWorkouts({ preserveSearchFocus: 'modal' });
}

/**
 * Helper to check if a planned workout matches a search query by name or station splits.
 * @param {Object} plan
 * @param {string} query
 * @returns {boolean}
 */
function matchesPlanSearch(plan, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  if (String(plan?.name || '').toLowerCase().includes(q)) return true;
  const stages = Array.isArray(plan?.stages) ? plan.stages : [];
  return stages.some(s => String(s?.stationName || s?.name || s?.type || '').toLowerCase().includes(q));
}

/**
 * Renders saved planned routines into both:
 * 1. `#dashboard-planned-routines` (Sleek 1-Line Quick-Launcher Bar + Collapsible Scroll-Capped Drawer)
 * 2. `#modal-saved-plans-list` (Compact Scroll-Capped Library inside the Plan Routine modal)
 *
 * @param {Object} [options={}]
 */
export function renderSavedPlannedWorkouts(options = {}) {
  if (typeof document === 'undefined') return;
  const plans = Array.isArray(store.plannedWorkouts) ? store.plannedWorkouts : [];

  const dashContainer = document.getElementById('dashboard-planned-routines');
  const modalListContainer = document.getElementById('modal-saved-plans-list');
  const modalSavedCount = document.getElementById('modal-saved-plans-count');
  const modalSearchInput = document.getElementById('modal-saved-plans-search');

  if (modalSavedCount) {
    modalSavedCount.textContent = String(plans.length);
  }
  if (modalSearchInput) {
    modalSearchInput.style.display = plans.length > 1 ? 'inline-block' : 'none';
    if (options.preserveSearchFocus !== 'modal' && modalSearchInput.value !== modalPlanSearchQuery) {
      modalSearchInput.value = modalPlanSearchQuery;
    }
  }

  // Ensure selectedDashboardPlanId resolves to an existing routine
  if (plans.length > 0) {
    const exists = plans.some(p => p && String(p.id) === String(selectedDashboardPlanId));
    if (!exists) {
      selectedDashboardPlanId = String(plans[0].id);
    }
  } else {
    selectedDashboardPlanId = null;
    isDashboardDrawerOpen = false;
  }

  // 1. Render Sleek 1-Line Quick-Launcher Bar + Collapsible Compact Drawer on Dashboard
  if (dashContainer) {
    if (plans.length === 0) {
      dashContainer.style.display = 'none';
      dashContainer.innerHTML = '';
    } else {
      dashContainer.style.display = 'flex';
      const activePlan = plans.find(p => p && String(p.id) === String(selectedDashboardPlanId)) || plans[0];
      const activeStages = Array.isArray(activePlan.stages) ? activePlan.stages : [];
      const activeSec = Number(activePlan.durationSeconds) || activeStages.reduce((acc, s) => acc + ((s.durationMin || 0) * 60 + (s.durationSec || 0) || s.duration || 0), 0);
      const activeTimeStr = formatPlanDuration(activeSec);
      const activeDistM = Number(activePlan.totalDistanceMeters) || 0;
      const activeDistStr = activeDistM > 0 ? ` • ${activeDistM >= 1000 ? `${(activeDistM / 1000).toFixed(1)}km` : `${activeDistM}m`}` : '';
      const activeTooltip = buildCompactStageSequenceText(activeStages);

      const filteredDashPlans = plans.filter(p => matchesPlanSearch(p, dashboardPlanSearchQuery));

      dashContainer.innerHTML = `
        <div class="dashboard-quick-plan-bar" style="display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; width: 100%; background: rgba(15, 23, 42, 0.55); border: 1px solid rgba(99, 102, 241, 0.22); border-radius: 10px; padding: 7px 11px;">
          <div style="display: flex; align-items: center; gap: 8px; flex: 1; min-width: 220px;">
            <span id="dashboard-planned-count-badge" style="font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #a5b4fc; background: rgba(99, 102, 241, 0.14); border: 1px solid rgba(99, 102, 241, 0.3); padding: 3px 8px; border-radius: 6px; white-space: nowrap;">
              📋 Saved (${plans.length})
            </span>
            <select
              id="dashboard-saved-plan-select"
              aria-label="Select Saved Planned Routine"
              title="${escapeHtml(activeTooltip)}"
              style="flex: 1; min-width: 140px; max-width: 320px; background: rgba(0, 0, 0, 0.35); border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 7px; color: var(--text-primary); font-size: 12px; font-weight: 600; padding: 5px 10px; cursor: pointer; outline: none;"
            >
              ${plans.map((p) => {
                const st = Array.isArray(p.stages) ? p.stages : [];
                const sec = Number(p.durationSeconds) || st.reduce((acc, s) => acc + ((s.durationMin || 0) * 60 + (s.durationSec || 0) || s.duration || 0), 0);
                const tStr = formatPlanDuration(sec);
                const dM = Number(p.totalDistanceMeters) || 0;
                const dStr = dM > 0 ? ` • ${dM >= 1000 ? `${(dM / 1000).toFixed(1)}km` : `${dM}m`}` : '';
                const isSel = String(p.id) === String(activePlan.id) ? 'selected' : '';
                return `<option value="${escapeHtml(p.id)}" ${isSel}>${escapeHtml(p.name || 'Planned Routine')} — ⏱️ ${tStr} (${st.length} splits${dStr})</option>`;
              }).join('')}
            </select>
            <div class="dashboard-quick-plan-preview" title="${escapeHtml(activeTooltip)}" style="display: flex; align-items: center; gap: 6px; overflow: hidden;">
              <span style="font-size: 11px; font-weight: 700; color: #10b981; background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.25); padding: 2px 7px; border-radius: 999px; font-family: monospace; white-space: nowrap;">⏱️ ${activeTimeStr}</span>
              <span style="font-size: 11px; color: var(--text-muted); white-space: nowrap;">${activeStages.length} splits${activeDistStr}</span>
              ${buildSavedPlanStagePillsHtml(activeStages, 3)}
            </div>
          </div>

          <div style="display: flex; align-items: center; gap: 6px; flex-shrink: 0;">
            <button
              type="button"
              class="btn-send-plan-watch"
              data-plan-id="${escapeHtml(activePlan.id)}"
              title="Send selected routine directly to Apple Watch"
              style="padding: 5px 11px; font-size: 11.5px; font-weight: 700; border-radius: 7px; border: 1px solid rgba(16, 185, 129, 0.45); background: linear-gradient(135deg, rgba(16, 185, 129, 0.22) 0%, rgba(5, 150, 105, 0.34) 100%); color: #34d399; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;"
            >⌚ Send to Watch</button>
            <button
              type="button"
              class="btn-edit-plan"
              data-plan-id="${escapeHtml(activePlan.id)}"
              title="Edit selected routine"
              style="padding: 5px 9px; font-size: 11.5px; font-weight: 600; border-radius: 7px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.05); color: var(--text-primary); cursor: pointer; white-space: nowrap;"
            >✏️ Edit</button>
            <button
              type="button"
              class="btn-delete-plan"
              data-plan-id="${escapeHtml(activePlan.id)}"
              title="Delete selected routine"
              style="padding: 5px 8px; font-size: 11.5px; font-weight: 600; border-radius: 7px; border: 1px solid rgba(244, 63, 94, 0.3); background: rgba(244, 63, 94, 0.1); color: #f43f5e; cursor: pointer;"
            >🗑️</button>
            <button
              type="button"
              id="btn-toggle-dashboard-plans-drawer"
              title="Expand or collapse compact routine list"
              style="padding: 5px 9px; font-size: 11px; font-weight: 600; border-radius: 7px; border: 1px solid rgba(99, 102, 241, 0.3); background: rgba(99, 102, 241, 0.1); color: #a5b4fc; cursor: pointer; white-space: nowrap;"
            >${isDashboardDrawerOpen ? '▴ Hide' : `▾ All (${plans.length})`}</button>
          </div>
        </div>

        <div
          id="dashboard-plans-drawer"
          style="display: ${isDashboardDrawerOpen ? 'flex' : 'none'}; flex-direction: column; gap: 6px; margin-top: 8px; padding: 8px 10px; border-radius: 9px; background: rgba(3, 7, 18, 0.55); border: 1px solid rgba(255, 255, 255, 0.07); width: 100%;"
        >
          ${plans.length > 2 ? `
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
              <input
                type="search"
                id="dashboard-plan-search-input"
                value="${escapeHtml(dashboardPlanSearchQuery)}"
                placeholder="🔍 Filter ${plans.length} saved routines by name or station..."
                style="flex: 1; background: rgba(0, 0, 0, 0.35); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 6px; padding: 4px 9px; color: var(--text-primary); font-size: 11.5px; outline: none;"
              >
              <span style="font-size: 10.5px; color: var(--text-muted); white-space: nowrap;">Showing ${filteredDashPlans.length} of ${plans.length}</span>
            </div>
          ` : ''}
          <div id="dashboard-planned-routines-list" class="compact-plans-scroll" style="display: flex; flex-direction: column; gap: 5px; max-height: 175px; overflow-y: auto; padding-right: 3px;">
            ${filteredDashPlans.length === 0 ? `
              <div style="text-align: center; padding: 8px; font-size: 11.5px; color: var(--text-muted);">No routines match "${escapeHtml(dashboardPlanSearchQuery)}".</div>
            ` : filteredDashPlans.map((p) => {
              const stages = Array.isArray(p.stages) ? p.stages : [];
              const totalSec = Number(p.durationSeconds) || stages.reduce((acc, s) => acc + ((s.durationMin || 0) * 60 + (s.durationSec || 0) || s.duration || 0), 0);
              const totalTimeStr = formatPlanDuration(totalSec);
              const distM = Number(p.totalDistanceMeters) || 0;
              const distStr = distM > 0 ? ` • ${distM >= 1000 ? `${(distM / 1000).toFixed(1)}km` : `${distM}m`}` : '';
              const isSelected = String(p.id) === String(activePlan.id);
              const seqText = buildCompactStageSequenceText(stages);
              return `
                <div
                  class="saved-plan-card saved-plan-card-compact"
                  data-plan-id="${escapeHtml(p.id)}"
                  title="${escapeHtml(seqText)}"
                  style="padding: 6px 10px; border-radius: 7px; background: ${isSelected ? 'rgba(99, 102, 241, 0.14)' : 'rgba(255, 255, 255, 0.025)'}; border: 1px solid ${isSelected ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.07)'}; display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: nowrap;"
                >
                  <div style="display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1; overflow: hidden;">
                    <strong style="font-size: 12.5px; color: var(--text-primary); font-family: 'Outfit', sans-serif; white-space: nowrap;">${escapeHtml(p.name || 'Planned Routine')}</strong>
                    <span style="font-size: 10.5px; font-weight: 700; color: #10b981; background: rgba(16, 185, 129, 0.12); padding: 1px 6px; border-radius: 999px; font-family: monospace; white-space: nowrap;">⏱️ ${totalTimeStr}</span>
                    <span style="font-size: 11px; color: var(--text-muted); white-space: nowrap;">${stages.length} splits${distStr}</span>
                    <span style="font-size: 10.5px; color: var(--text-secondary); opacity: 0.75; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${escapeHtml(seqText)}</span>
                  </div>
                  <div style="display: flex; align-items: center; gap: 5px; flex-shrink: 0;">
                    <button
                      type="button"
                      class="btn-send-plan-watch"
                      data-plan-id="${escapeHtml(p.id)}"
                      style="padding: 4px 8px; font-size: 11px; font-weight: 700; border-radius: 6px; border: 1px solid rgba(16, 185, 129, 0.4); background: rgba(16, 185, 129, 0.16); color: #34d399; cursor: pointer; white-space: nowrap;"
                    >⌚ Send</button>
                    <button
                      type="button"
                      class="btn-edit-plan"
                      data-plan-id="${escapeHtml(p.id)}"
                      style="padding: 4px 8px; font-size: 11px; font-weight: 600; border-radius: 6px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.05); color: var(--text-primary); cursor: pointer;"
                    >✏️</button>
                    <button
                      type="button"
                      class="btn-delete-plan"
                      data-plan-id="${escapeHtml(p.id)}"
                      style="padding: 4px 7px; font-size: 11px; font-weight: 600; border-radius: 6px; border: 1px solid rgba(244, 63, 94, 0.3); background: rgba(244, 63, 94, 0.1); color: #f43f5e; cursor: pointer;"
                    >🗑️</button>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;

      if (options.preserveSearchFocus === 'dashboard') {
        const searchEl = document.getElementById('dashboard-plan-search-input');
        if (searchEl && typeof searchEl.focus === 'function') {
          searchEl.focus();
          const len = searchEl.value.length;
          if (typeof searchEl.setSelectionRange === 'function') searchEl.setSelectionRange(len, len);
        }
      }
    }
  }

  // 2. Render Compact Scroll-Capped Library inside Modal
  if (modalListContainer) {
    if (plans.length === 0) {
      modalListContainer.innerHTML = `
        <div style="text-align: center; padding: 12px; font-size: 12px; color: var(--text-muted); border: 1px dashed rgba(255,255,255,0.08); border-radius: 8px;">
          No saved routines yet. Build a routine above and click <strong>Save Routine</strong>!
        </div>
      `;
    } else {
      const filteredModalPlans = plans.filter(p => matchesPlanSearch(p, modalPlanSearchQuery));
      if (filteredModalPlans.length === 0) {
        modalListContainer.innerHTML = `
          <div style="text-align: center; padding: 10px; font-size: 11.5px; color: var(--text-muted); border: 1px dashed rgba(255,255,255,0.08); border-radius: 8px;">
            No saved routines match "${escapeHtml(modalPlanSearchQuery)}".
          </div>
        `;
      } else {
        modalListContainer.innerHTML = filteredModalPlans.map((p) => {
          const stages = Array.isArray(p.stages) ? p.stages : [];
          const totalSec = Number(p.durationSeconds) || stages.reduce((acc, s) => acc + ((s.durationMin || 0) * 60 + (s.durationSec || 0) || s.duration || 0), 0);
          const totalTimeStr = formatPlanDuration(totalSec);
          const distM = Number(p.totalDistanceMeters) || 0;
          const distStr = distM > 0 ? ` • ${distM >= 1000 ? `${(distM / 1000).toFixed(1)}km` : `${distM}m`}` : '';
          const isCurrent = String(currentEditingPlanId) === String(p.id);
          const seqText = buildCompactStageSequenceText(stages);
          return `
            <div
              class="modal-saved-plan-item"
              data-plan-id="${escapeHtml(p.id)}"
              title="${escapeHtml(seqText)}"
              style="padding: 7px 10px; border-radius: 8px; background: ${isCurrent ? 'rgba(99, 102, 241, 0.14)' : 'rgba(255,255,255,0.03)'}; border: 1px solid ${isCurrent ? 'rgba(99, 102, 241, 0.45)' : 'rgba(255,255,255,0.08)'}; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: nowrap;"
            >
              <div style="min-width: 0; flex: 1; display: flex; align-items: center; gap: 8px; overflow: hidden;">
                <strong style="font-size: 12.5px; color: var(--text-primary); white-space: nowrap;">${escapeHtml(p.name || 'Planned Routine')}</strong>
                <span style="font-size: 10.5px; color: #10b981; font-family: monospace; font-weight: 700; background: rgba(16, 185, 129, 0.12); padding: 1px 6px; border-radius: 999px; white-space: nowrap;">⏱️ ${totalTimeStr}</span>
                <span style="font-size: 11px; color: var(--text-muted); white-space: nowrap;">${stages.length} splits${distStr}</span>
                <span style="font-size: 10.5px; color: var(--text-secondary); opacity: 0.75; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${escapeHtml(seqText)}</span>
              </div>
              <div style="display: flex; align-items: center; gap: 5px; flex-shrink: 0;">
                <button
                  type="button"
                  class="btn-send-plan-watch"
                  data-plan-id="${escapeHtml(p.id)}"
                  style="padding: 4px 9px; font-size: 11px; font-weight: 700; border-radius: 6px; border: 1px solid rgba(16, 185, 129, 0.4); background: rgba(16, 185, 129, 0.18); color: #34d399; cursor: pointer; white-space: nowrap;"
                >⌚ Send</button>
                <button
                  type="button"
                  class="btn-edit-plan"
                  data-plan-id="${escapeHtml(p.id)}"
                  style="padding: 4px 9px; font-size: 11px; font-weight: 600; border-radius: 6px; border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.06); color: var(--text-primary); cursor: pointer; white-space: nowrap;"
                >✏️ Load</button>
                <button
                  type="button"
                  class="btn-delete-plan"
                  data-plan-id="${escapeHtml(p.id)}"
                  style="padding: 4px 7px; font-size: 11px; font-weight: 600; border-radius: 6px; border: 1px solid rgba(244, 63, 94, 0.3); background: rgba(244, 63, 94, 0.1); color: #f43f5e; cursor: pointer;"
                >🗑️</button>
              </div>
            </div>
          `;
        }).join('');
      }
    }
  }
}

/**
 * Updates the AI Coach readiness context indicator on the Dashboard Workout Generator card (#dashboard-ai-readiness-callout).
 */
export function updateDashboardAIReadinessCallout() {
  if (typeof document === 'undefined') return;
  const calloutEl = document.getElementById('dashboard-ai-readiness-callout');
  if (!calloutEl) return;

  const dailyRec = getDailySubjectiveRecovery() || store.todayHealth;
  const hasTelemetry = dailyRec && (
    dailyRec.restingHr !== undefined || dailyRec.restingHR !== undefined ||
    dailyRec.sleepHours !== undefined || dailyRec.sleepScore !== undefined ||
    dailyRec.physicalFeeling !== undefined || dailyRec.mentalFeeling !== undefined ||
    dailyRec.hrv !== undefined
  );

  if (!hasTelemetry) {
    calloutEl.style.display = 'flex';
    calloutEl.innerHTML = `
      <span class="badge-readiness-callout" style="padding: 2px 8px; border-radius: 12px; font-weight: 600; font-size: 10px; background: rgba(255, 255, 255, 0.05); border: 1px solid var(--border-light); color: var(--text-muted); cursor: pointer;" onclick="window.PulseFit?.showView?.('view-performance') || window.showView?.('view-performance')">
        ℹ️ Standard Baseline
      </span>
      <span style="color: var(--text-muted); font-size: 11px;">
        ${escapeHtml("No daily recovery logged. Tap 'Performance > ✏️ My status' to personalize coaching intensity.")}
      </span>
    `;
    return;
  }

  const perfMetrics = calculatePerformanceMetrics();
  const tsbNorm = Math.min(100, Math.max(0, ((perfMetrics.form + 25) / 50) * 100));
  const acuteRec = calculateAcuteRecoveryScore(dailyRec);
  const readinessScore = Math.round(0.4 * tsbNorm + 0.6 * acuteRec);

  let badgeColor = '';
  let badgeBg = '';
  let badgeBorder = '';
  let dotEmoji = '🟢';
  let tierLabel = 'Peak Readiness';
  let copyText = '';

  const rhr = dailyRec.restingHr ?? dailyRec.restingHR;
  const sleepH = dailyRec.sleepHours !== undefined && dailyRec.sleepHours !== null
    ? (Number(dailyRec.sleepHours) + (dailyRec.sleepMinutes ? Number(dailyRec.sleepMinutes) / 60 : 0)).toFixed(1)
    : null;

  if (readinessScore >= 80) {
    tierLabel = 'Peak Readiness';
    dotEmoji = '🟢';
    badgeColor = '#10b981';
    badgeBg = 'rgba(16, 185, 129, 0.12)';
    badgeBorder = '1px solid rgba(16, 185, 129, 0.35)';
    copyText = `Calibrated: AI Coach tuned to your ${sleepH ? sleepH + 'h sleep' : 'readiness'}${rhr ? ' & ' + rhr + ' bpm RHR' : ''}`;
  } else if (readinessScore >= 60) {
    tierLabel = 'High Readiness';
    dotEmoji = '🟢';
    badgeColor = '#0ea5e9';
    badgeBg = 'rgba(14, 165, 233, 0.12)';
    badgeBorder = '1px solid rgba(14, 165, 233, 0.35)';
    copyText = `Calibrated: AI Coach tuned to your ${sleepH ? sleepH + 'h sleep' : 'readiness'}${rhr ? ' & ' + rhr + ' bpm RHR' : ''}`;
  } else if (readinessScore >= 40) {
    tierLabel = 'Moderate Readiness';
    dotEmoji = '🟡';
    badgeColor = '#f59e0b';
    badgeBg = 'rgba(245, 158, 11, 0.12)';
    badgeBorder = '1px solid rgba(245, 158, 11, 0.35)';
    copyText = `Moderate Load: Balanced intensity recommended (TSB ${perfMetrics.form >= 0 ? '+' : ''}${perfMetrics.form.toFixed(1)})`;
  } else {
    tierLabel = 'Sub-Optimal Recovery';
    dotEmoji = '🔴';
    badgeColor = '#f43f5e';
    badgeBg = 'rgba(244, 63, 94, 0.12)';
    badgeBorder = '1px solid rgba(244, 63, 94, 0.35)';
    copyText = `Fatigue Alert: AI Coach scaled routine to Zone 2 active recovery & mobility`;
  }

  calloutEl.style.display = 'flex';
  calloutEl.innerHTML = `
    <span class="badge-readiness-callout" style="padding: 2px 8px; border-radius: 12px; font-weight: 600; font-size: 10px; background: ${badgeBg}; border: ${badgeBorder}; color: ${badgeColor}; flex-shrink: 0;">
      ${escapeHtml(`${dotEmoji} ${readinessScore}% ${tierLabel}`)}
    </span>
    <span style="color: var(--text-muted); font-size: 11px;">
      ${escapeHtml(copyText)}
    </span>
  `;
}

/**
 * Generates an AI Workout Recommendation when #btn-get-recommendation is clicked.
 */
export async function generateWorkoutRecommendation(providedMinutes) {
  if (typeof document === 'undefined') return;
  const durInput = document.getElementById('input-rec-duration');
  const targetMinutes = providedMinutes || Math.max(10, Math.min(240, parseInt(durInput?.value, 10) || 45));

  const lblDuration = document.getElementById('lbl-rec-target-duration');
  const lblDay = document.getElementById('lbl-rec-day-of-week');
  const lblDaysLeft = document.getElementById('lbl-rec-days-left');
  const textContainer = document.getElementById('ai-rec-text-container');
  const statusBadge = document.getElementById('ai-rec-status-badge');

  const now = new Date();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayIdx = now.getDay();
  const daysLeft = dayIdx === 0 ? 1 : (8 - dayIdx);

  if (lblDuration) lblDuration.textContent = `${targetMinutes} min`;
  if (lblDay) lblDay.textContent = dayNames[dayIdx];
  if (lblDaysLeft) lblDaysLeft.textContent = `${daysLeft} days`;

  showView('view-ai-recommendation');

  if (textContainer) {
    textContainer.innerHTML = `<div style="color: var(--text-muted); font-size: 13px;">🤖 Generating ${targetMinutes}-minute routine optimized for your weekly targets...</div>`;
  }
  if (statusBadge) statusBadge.textContent = 'Generating...';

  try {
    const dailyRec = getDailySubjectiveRecovery() || store.todayHealth || {};
    const perfMetrics = calculatePerformanceMetrics();
    const tsbNorm = Math.min(100, Math.max(0, ((perfMetrics.form + 25) / 50) * 100));
    const acuteRec = calculateAcuteRecoveryScore(dailyRec);
    const readinessScore = Math.round(0.4 * tsbNorm + 0.6 * acuteRec);

    let readinessStatus = 'Moderate Readiness';
    if (readinessScore >= 80) readinessStatus = 'Peak Readiness';
    else if (readinessScore >= 60) readinessStatus = 'High Readiness';
    else if (readinessScore >= 40) readinessStatus = 'Moderate Readiness';
    else readinessStatus = 'Sub-Optimal Recovery';

    const sleepHoursVal = dailyRec.sleepHours !== undefined && dailyRec.sleepHours !== null
      ? (Number(dailyRec.sleepHours) + (dailyRec.sleepMinutes ? Number(dailyRec.sleepMinutes) / 60 : 0))
      : null;

    const recoveryData = {
      readinessScore,
      readinessStatus,
      fitness: perfMetrics.fitness,
      fatigue: perfMetrics.fatigue,
      form: Math.round(perfMetrics.form * 10) / 10,
      restingHr: dailyRec.restingHr ?? dailyRec.restingHR ?? null,
      hrv: dailyRec.hrv ?? null,
      sleepHours: sleepHoursVal,
      sleepScore: dailyRec.sleepScore ?? null,
      physicalFeeling: dailyRec.physicalFeeling ?? null,
      mentalFeeling: dailyRec.mentalFeeling ?? null
    };

    const recentWorkouts = (store.workouts || []).slice(0, 5);
    const tel = recentWorkouts.map(w => generateTelemetryPrompt(w, isDistanceStation)).join('\n---\n') || 'No recent workouts logged this week.';
    const todayStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
    const prompt = generateRecommendationPrompt(tel, targetMinutes, todayStr, store.geminiInstructions, recoveryData);
    const supabase = (typeof window !== 'undefined' && window.supabaseClient) || store.supabaseClient;
    const result = await callGeminiCoach(prompt, {
      supabaseClient: supabase,
      customKey: store.geminiApiKey,
      systemInstruction: store.geminiInstructions
    });
    if (textContainer) {
      textContainer.innerHTML = escapeHtml(result || 'No recommendation returned.').replace(/\n/g, '<br>');
    }
    if (statusBadge) statusBadge.textContent = 'Ready';
  } catch (err) {
    if (textContainer) {
      textContainer.innerHTML = `<div style="color: #f87171;">Unable to generate AI routine: ${escapeHtml(err?.message || 'Service unavailable')}</div>`;
    }
    if (statusBadge) statusBadge.textContent = 'Offline / Ready';
  }
}

/**
 * Functional alias for generateWorkoutRecommendation.
 */
export async function showAIRecommendation(targetMinutes) {
  return generateWorkoutRecommendation(targetMinutes);
}

/**
 * Sets up delegated event listeners for the Plan Routine modal and Dashboard saved routines.
 */
export function initPlannerView() {
  if (typeof document === 'undefined') return;
  if (isPlannerInitialized) return;
  isPlannerInitialized = true;

  subscribeKey('plannedWorkouts', () => {
    renderSavedPlannedWorkouts();
  });
  subscribeKey('dailyRecovery', () => {
    updateDashboardAIReadinessCallout();
  });
  subscribeKey('todayHealth', () => {
    updateDashboardAIReadinessCallout();
  });

  updateDashboardAIReadinessCallout();

  const openBtn = document.getElementById('btn-plan-routine');
  if (openBtn) {
    openBtn.addEventListener('click', () => openPlanRoutineModal(null));
  }

  const genBtn = document.getElementById('btn-get-recommendation');
  if (genBtn && !genBtn._recBound) {
    genBtn._recBound = true;
    genBtn.addEventListener('click', () => generateWorkoutRecommendation());
  }

  const backRecBtn = document.getElementById('btn-back-from-ai-recommendation');
  if (backRecBtn) {
    backRecBtn.addEventListener('click', () => showView('view-dashboard'));
  }

  const modal = document.getElementById('modal-plan-routine');
  if (modal) {
    const closeBtn = document.getElementById('btn-close-plan-routine-modal');
    const cancelBtn = document.getElementById('btn-cancel-plan-routine');
    const saveBtn = document.getElementById('btn-save-planned-routine');
    const sendWatchBtn = document.getElementById('btn-send-planned-routine-watch');
    const newDraftBtn = document.getElementById('btn-new-plan-draft');
    const clearSplitsBtn = document.getElementById('btn-clear-plan-splits');
    const modalSearchEl = document.getElementById('modal-saved-plans-search');

    if (closeBtn) closeBtn.addEventListener('click', closePlanRoutineModal);
    if (cancelBtn) cancelBtn.addEventListener('click', closePlanRoutineModal);
    if (saveBtn) saveBtn.addEventListener('click', () => savePlannedWorkout({ closeAfterSave: false }));
    if (sendWatchBtn) sendWatchBtn.addEventListener('click', () => sendPlannedWorkoutToWatch(null));
    if (newDraftBtn) newDraftBtn.addEventListener('click', startNewPlanRoutineDraft);
    if (clearSplitsBtn) clearSplitsBtn.addEventListener('click', clearPlanSplits);
    if (modalSearchEl) {
      modalSearchEl.addEventListener('input', (e) => {
        setModalPlanSearchQuery(e.target.value);
      });
    }

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closePlanRoutineModal();
    });

    // Quick-add split buttons
    const addRunBtn = document.getElementById('btn-plan-add-run');
    const addRoxBtn = document.getElementById('btn-plan-add-roxzone');
    const addStationBtn = document.getElementById('btn-plan-add-station');
    const addRestBtn = document.getElementById('btn-plan-add-rest');
    const addHyroxPresetBtn = document.getElementById('btn-plan-add-hyrox-preset');

    if (addRunBtn) addRunBtn.addEventListener('click', () => addPlanSplit('Run', 'Run'));
    if (addRoxBtn) addRoxBtn.addEventListener('click', () => addPlanSplit('RoxZone', 'RoxZone'));
    if (addStationBtn) {
      addStationBtn.addEventListener('click', () => {
        const quickStationSelect = document.getElementById('plan-quick-station-select');
        const chosenStation = quickStationSelect && quickStationSelect.value ? quickStationSelect.value : 'Auto';
        addPlanSplit('Station', chosenStation);
      });
    }
    if (addRestBtn) addRestBtn.addEventListener('click', () => addPlanSplit('Rest', 'Rest'));
    if (addHyroxPresetBtn) addHyroxPresetBtn.addEventListener('click', () => loadHyroxPresetIntoPlanner());

    // Delegated listeners for interactive split rows
    const splitsList = document.getElementById('plan-splits-list');
    if (splitsList) {
      splitsList.addEventListener('change', (e) => {
        const target = e.target;
        if (!target) return;
        const idx = parseInt(target.getAttribute('data-index'), 10);
        if (isNaN(idx)) return;

        if (target.classList.contains('plan-split-type-select')) {
          updatePlanSplitField(idx, 'type', target.value);
        } else if (target.classList.contains('plan-split-station-select')) {
          updatePlanSplitField(idx, 'stationName', target.value);
        }
      });

      splitsList.addEventListener('input', (e) => {
        const target = e.target;
        if (!target) return;
        const idx = parseInt(target.getAttribute('data-index'), 10);
        if (isNaN(idx)) return;

        if (target.classList.contains('plan-split-min-input')) {
          updatePlanSplitField(idx, 'durationMin', target.value);
        } else if (target.classList.contains('plan-split-sec-input')) {
          updatePlanSplitField(idx, 'durationSec', target.value);
        } else if (target.classList.contains('plan-split-metric-input')) {
          updatePlanSplitField(idx, 'metricValue', target.value);
        }
      });

      splitsList.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        const idx = parseInt(btn.getAttribute('data-index'), 10);
        if (isNaN(idx)) return;

        if (btn.classList.contains('btn-plan-step')) {
          const field = btn.getAttribute('data-field');
          const delta = parseFloat(btn.getAttribute('data-delta'));
          if (field && !isNaN(delta)) {
            stepPlanSplitField(idx, field, delta);
          }
        } else if (btn.classList.contains('btn-plan-split-up')) {
          movePlanSplit(idx, -1);
        } else if (btn.classList.contains('btn-plan-split-down')) {
          movePlanSplit(idx, 1);
        } else if (btn.classList.contains('btn-plan-split-dup')) {
          duplicatePlanSplit(idx);
        } else if (btn.classList.contains('btn-plan-split-remove')) {
          removePlanSplit(idx);
        }
      });
    }
  }

  // Delegated handlers for Saved Planned Routine controls (both Dashboard and Modal)
  const handleSavedPlanActions = (e) => {
    const toggleDrawerBtn = e.target.closest('#btn-toggle-dashboard-plans-drawer');
    if (toggleDrawerBtn) {
      e.stopPropagation();
      toggleDashboardPlansDrawer();
      return;
    }

    const sendBtn = e.target.closest('.btn-send-plan-watch');
    if (sendBtn) {
      e.stopPropagation();
      const planId = sendBtn.getAttribute('data-plan-id');
      if (planId) sendPlannedWorkoutToWatch(planId);
      return;
    }

    const editBtn = e.target.closest('.btn-edit-plan');
    if (editBtn) {
      e.stopPropagation();
      const planId = editBtn.getAttribute('data-plan-id');
      if (planId) openPlanRoutineModal(planId);
      return;
    }

    const delBtn = e.target.closest('.btn-delete-plan');
    if (delBtn) {
      e.stopPropagation();
      const planId = delBtn.getAttribute('data-plan-id');
      if (planId) deletePlannedWorkout(planId);
    }
  };

  const dashSavedContainer = document.getElementById('dashboard-planned-routines');
  if (dashSavedContainer) {
    dashSavedContainer.addEventListener('click', handleSavedPlanActions);
    dashSavedContainer.addEventListener('change', (e) => {
      if (e.target && e.target.id === 'dashboard-saved-plan-select') {
        selectDashboardPlannedWorkout(e.target.value);
      }
    });
    dashSavedContainer.addEventListener('input', (e) => {
      if (e.target && e.target.id === 'dashboard-plan-search-input') {
        setDashboardPlanSearchQuery(e.target.value);
      }
    });
  }

  const modalSavedContainer = document.getElementById('modal-saved-plans-list');
  if (modalSavedContainer) {
    modalSavedContainer.addEventListener('click', handleSavedPlanActions);
  }

  renderSavedPlannedWorkouts();
}

export function resetPlannerInitialization() {
  isPlannerInitialized = false;
  currentEditingPlanId = null;
  draftPlanSplits = [];
  selectedDashboardPlanId = null;
  isDashboardDrawerOpen = false;
  dashboardPlanSearchQuery = '';
  modalPlanSearchQuery = '';
}
